import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getCirugiasByMes,
  getCirugiasByFecha,
  getHospital,
  getTipoCirugia,
  Cirugia,
  Hospital,
  TipoCirugia,
} from '../../services/cirugiasService';
import DetalleCirugiaModal from './DetalleCirugiaModal';
import FormCirugiaModal from './FormCirugiaModal';
import medicoVfpService, { PacienteVfp } from '../../services/medicoVfpService';

const estadoLabels: Record<string, string> = {
  prospecto: 'Por confirmar',
  confirmada: 'Confirmada',
  en_proceso: 'En proceso',
  concluida: 'Concluida',
  pospuesta: 'Pospuesta',
};

export default function CalendarioCirugias() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [cirugias, setCirugias] = useState<Cirugia[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [cirugiasDia, setCirugiasDia] = useState<Cirugia[]>([]);
  const [selectedCirugia, setSelectedCirugia] = useState<Cirugia | null>(null);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [hospitalesMap, setHospitalesMap] = useState<Record<number, Hospital>>({});
  const [tiposCirugiaMap, setTiposCirugiaMap] = useState<Record<number, TipoCirugia>>({});
  const [pacientesMap, setPacientesMap] = useState<Record<number, PacienteVfp>>({});
  const [detallesLoading, setDetallesLoading] = useState(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    loadCirugiasMes();
  }, [year, month]);

  const loadCirugiasMes = async () => {
    try {
      const response = await getCirugiasByMes(year, month + 1);
      // El backend puede devolver un array directamente o un objeto { data: [...] }
      const data = Array.isArray(response)
        ? response
        : Array.isArray(response.data)
        ? response.data
        : (response.data?.data || []);
      setCirugias(data);
    } catch (error) {
      console.error('Error al cargar cirugías:', error);
      toast.error('Error al cargar cirugías del mes');
    }
  };

  const handleDateClick = async (date: Date) => {
    setSelectedDate(date);
    const fechaStr = date.toISOString().split('T')[0];
    
    try {
      const response = await getCirugiasByFecha(fechaStr);
      const data = Array.isArray(response)
        ? response
        : Array.isArray(response.data)
        ? response.data
        : (response.data?.data || []);
      setCirugiasDia(data);
      
      if (data.length === 1) {
        setSelectedCirugia(data[0]);
        setShowDetalleModal(true);
      }
    } catch (error) {
      console.error('Error al cargar cirugías del día:', error);
      toast.error('Error al cargar cirugías del día');
    }
  };

  const handleCirugiaClick = (cirugia: Cirugia) => {
    setSelectedCirugia(cirugia);
    setShowDetalleModal(true);
  };

  const loadDetallesCirugias = useCallback(async (lista: Cirugia[]) => {
    if (!lista.length) return;

    const unique = (values: Array<number | null | undefined>) =>
      Array.from(new Set(values.filter((v): v is number => typeof v === 'number')));

    const missingHospitals = unique(lista.map((c) => c.hospitalId).filter((id) => id && !hospitalesMap[id]));
    const missingTipos = unique(lista.map((c) => c.tipoCirugiaId).filter((id) => id && !tiposCirugiaMap[id]));
    const missingPacientes = unique(lista.map((c) => c.pacienteId).filter((id) => id && !pacientesMap[id]));

    if (!missingHospitals.length && !missingTipos.length && !missingPacientes.length) {
      return;
    }

    setDetallesLoading(true);
    try {
      const [hospitales, tipos, pacientes] = await Promise.all([
        Promise.all(
          missingHospitals.map(async (id) => {
            try {
              const data = await getHospital(id);
              return { id, data };
            } catch (error) {
              console.error('Error al cargar hospital', id, error);
              return null;
            }
          })
        ),
        Promise.all(
          missingTipos.map(async (id) => {
            try {
              const data = await getTipoCirugia(id);
              return { id, data };
            } catch (error) {
              console.error('Error al cargar tipo de cirugía', id, error);
              return null;
            }
          })
        ),
        Promise.all(
          missingPacientes.map(async (id) => {
            try {
              const data = await medicoVfpService.getPacienteVfp(id);
              return { id, data };
            } catch (error) {
              console.error('Error al cargar paciente', id, error);
              return null;
            }
          })
        ),
      ]);

      const filteredHospitals = hospitales.filter((h): h is { id: number; data: Hospital } => Boolean(h));
      const filteredTipos = tipos.filter((t): t is { id: number; data: TipoCirugia } => Boolean(t));
      const filteredPacientes = pacientes.filter((p): p is { id: number; data: PacienteVfp } => Boolean(p));

      if (filteredHospitals.length) {
        setHospitalesMap((prev) => {
          const updated = { ...prev };
          filteredHospitals.forEach(({ id, data }) => {
            updated[id] = data;
          });
          return updated;
        });
      }

      if (filteredTipos.length) {
        setTiposCirugiaMap((prev) => {
          const updated = { ...prev };
          filteredTipos.forEach(({ id, data }) => {
            updated[id] = data;
          });
          return updated;
        });
      }

      if (filteredPacientes.length) {
        setPacientesMap((prev) => {
          const updated = { ...prev };
          filteredPacientes.forEach(({ id, data }) => {
            updated[id] = data;
          });
          return updated;
        });
      }
    } finally {
      setDetallesLoading(false);
    }
  }, [hospitalesMap, tiposCirugiaMap, pacientesMap]);

  useEffect(() => {
    if (cirugiasDia.length > 0) {
      loadDetallesCirugias(cirugiasDia);
    }
  }, [cirugiasDia, loadDetallesCirugias]);

  const previousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const getDaysInMonth = () => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];

    // Días vacíos al inicio
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Días del mes
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getCirugiasForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return cirugias.filter((c) => {
      if (!c.fechaProgramada) return false;
      const cirugiaDate = new Date(c.fechaProgramada).toISOString().split('T')[0];
      return cirugiaDate === dateStr && c.estado !== 'cancelada';
    });
  };

  const getEstadoLabel = (estado: string) => estadoLabels[estado] || estado;

  const getColorForEstado = (estado: string) => {
    switch (estado) {
      case 'confirmada':   return 'bg-green-500';
      case 'prospecto':    return 'bg-yellow-400';
      case 'en_proceso':   return 'bg-blue-500';
      case 'concluida':    return 'bg-gray-400';
      case 'pospuesta':    return 'bg-orange-400';
      default:             return 'bg-gray-300';
    }
  };

  const getBgForDia = (cirugiasDelDia: Cirugia[]) => {
    if (cirugiasDelDia.some(c => c.estado === 'confirmada'))   return 'bg-green-50 hover:bg-green-100';
    if (cirugiasDelDia.some(c => c.estado === 'en_proceso'))   return 'bg-blue-50 hover:bg-blue-100';
    if (cirugiasDelDia.some(c => c.estado === 'prospecto'))    return 'bg-yellow-50 hover:bg-yellow-100';
    if (cirugiasDelDia.some(c => c.estado === 'pospuesta'))    return 'bg-orange-50 hover:bg-orange-100';
    return 'bg-gray-50 hover:bg-gray-100';
  };

  const days = getDaysInMonth();
  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  return (
    <div>
      {/* Header del Calendario */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={previousMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-bold text-gray-900">
            {monthNames[month]} {year}
          </h2>
          <button
            onClick={nextMonth}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        <button
          onClick={() => setShowFormModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nueva Cirugía
        </button>
      </div>

      {/* Calendario */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Nombres de los días */}
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
          {dayNames.map((day) => (
            <div
              key={day}
              className="py-3 text-center text-sm font-semibold text-gray-700"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Días del mes */}
        <div className="grid grid-cols-7">
          {days.map((date, index) => {
            if (!date) {
              return <div key={`empty-${index}`} className="h-24 border-b border-r border-gray-200" />;
            }

            const cirugiasDelDia = getCirugiasForDate(date);
            const hasConfirmedCirugias = cirugiasDelDia.length > 0;
            const isToday = date.toDateString() === new Date().toDateString();
            const isSelected = selectedDate?.toDateString() === date.toDateString();

            return (
              <div
                key={index}
                onClick={() => handleDateClick(date)}
                className={`
                  h-24 border-b border-r border-gray-200 p-2 cursor-pointer
                  transition-colors duration-150
                  ${hasConfirmedCirugias ? getBgForDia(cirugiasDelDia) : 'hover:bg-gray-50'}
                  ${isSelected ? 'ring-2 ring-blue-500 ring-inset' : ''}
                `}
              >
                <div className="flex flex-col h-full">
                  <span
                    className={`
                      text-sm font-medium
                      ${isToday ? 'bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center' : ''}
                      ${hasConfirmedCirugias && !isToday ? 'text-gray-900 font-bold' : 'text-gray-700'}
                    `}
                  >
                    {date.getDate()}
                  </span>
                  {hasConfirmedCirugias && (
                    <div className="mt-1 flex-1 space-y-0.5">
                      {cirugiasDelDia.slice(0, 2).map((c) => (
                        <div key={c.id} className="flex items-center gap-1">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${getColorForEstado(c.estado)}`} />
                          <span className="text-xs text-gray-700 truncate">
                            {getEstadoLabel(c.estado)}
                            {c.padecimiento ? ` · ${c.padecimiento}` : ''}
                          </span>
                        </div>
                      ))}
                      {cirugiasDelDia.length > 2 && (
                        <div className="text-xs text-gray-500">+{cirugiasDelDia.length - 2} más</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lista de cirugías del día seleccionado */}
      {selectedDate && cirugiasDia.length > 1 && (
        <div className="mt-6 bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-lg font-semibold mb-4">
            Cirugías del {selectedDate.toLocaleDateString('es-MX', { 
              day: 'numeric', 
              month: 'long', 
              year: 'numeric' 
            })}
          </h3>
          {detallesLoading && (
            <p className="text-sm text-gray-500 mb-2">Cargando detalles adicionales...</p>
          )}
          <div className="space-y-2">
            {cirugiasDia.map((cirugia) => (
              <div
                key={cirugia.id}
                onClick={() => handleCirugiaClick(cirugia)}
                className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">
                      {tiposCirugiaMap[cirugia.tipoCirugiaId]?.nombre || 'Tipo de cirugía no disponible'}
                    </p>
                    <p className="text-sm text-gray-700">
                      {(() => {
                        const paciente = pacientesMap[cirugia.pacienteId];
                        if (paciente) {
                          return `${paciente.nombre || ''} ${paciente.apellido1 || ''} ${paciente.apellido2 || ''}`.trim() || `Paciente #${cirugia.pacienteId}`;
                        }
                        return `Paciente #${cirugia.pacienteId}`;
                      })()}
                    </p>
                    {cirugia.padecimiento && (
                      <p className="text-xs text-gray-500 italic">{cirugia.padecimiento}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      Horario: {cirugia.horaInicio || 'Sin hora'} · Sala: {cirugia.salaQuirurgica || 'Sin sala'}
                    </p>
                    <p className="text-xs text-gray-500">
                      Hospital: {hospitalesMap[cirugia.hospitalId]?.nombre || 'No especificado'}
                    </p>
                  </div>
                  <span
                    className={`
                      px-3 py-1 rounded-full text-xs font-medium
                      ${cirugia.estado === 'confirmada' ? 'bg-green-100 text-green-800' : ''}
                      ${cirugia.estado === 'prospecto' ? 'bg-yellow-100 text-yellow-800' : ''}
                      ${cirugia.estado === 'en_proceso' ? 'bg-blue-100 text-blue-800' : ''}
                      ${cirugia.estado === 'concluida' ? 'bg-gray-100 text-gray-800' : ''}
                      ${cirugia.estado === 'pospuesta' ? 'bg-orange-100 text-orange-800' : ''}
                    `}
                  >
                    {getEstadoLabel(cirugia.estado)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modales */}
      {showDetalleModal && selectedCirugia && (
        <DetalleCirugiaModal
          cirugia={selectedCirugia}
          onClose={() => {
            setShowDetalleModal(false);
            setSelectedCirugia(null);
          }}
          onUpdate={loadCirugiasMes}
        />
      )}

      {showFormModal && (
        <FormCirugiaModal
          onClose={() => setShowFormModal(false)}
          onSuccess={() => {
            setShowFormModal(false);
            loadCirugiasMes();
          }}
        />
      )}
    </div>
  );
}
